import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import { useAuth } from "./AuthGate.js";
import { Icon } from "../components/Icon.js";
import { Modal } from "../components/Modal.js";
import { useToast } from "../components/Toast.js";

export function AccountMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  const initial = (user.displayName ?? user.username).charAt(0).toUpperCase();

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className="avatar-btn" onClick={() => setOpen((o) => !o)} aria-label="Account">
        {initial}
      </button>
      {open && (
        <div className="popover glass" style={{ right: 0, top: "calc(100% + 8px)", width: 220 }}>
          <div className="popover-head">
            <div className="strong">{user.displayName ?? user.username}</div>
            <div className="faint" style={{ fontSize: 12 }}>@{user.username}</div>
          </div>
          <button className="context-item" onClick={() => { setShowPassword(true); setOpen(false); }}>
            <Icon name="settings" size={14} /> <span>Change password</span>
          </button>
          <button className="context-item context-item-danger" onClick={() => void logout()}>
            <Icon name="logout" size={14} /> <span>Sign out</span>
          </button>
        </div>
      )}
      {showPassword && <ChangePasswordModal onClose={() => setShowPassword(false)} />}
    </div>
  );
}

function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.changePassword(current, next);
      toast.success(res.message ?? "Password updated.");
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Change password"
      onClose={onClose}
      width={420}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Updating…" : "Update password"}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="label">Current password</label>
        <input className="input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </div>
      <div className="field">
        <label className="label">New password</label>
        <input className="input" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
      </div>
      <div className="field">
        <label className="label">Confirm new password</label>
        <input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
      </div>
    </Modal>
  );
}
